import { z } from 'zod';
import type {
  VisitorReportDoc as PaidVisitorReportDoc,
  VisitorReportResponse as PaidVisitorReportResponse,
} from '../../paid-reports';

// Paid "Life Rebuild Report" — the one-time purchase offered on the Life Rebuild
// Score results page. Generated by the `life-rebuild-report-generator` task from the
// visitor's stored answers + free analysis, persisted to Firestore
// `visitor_reports/{runId}`, and rendered at /assessments/life-rebuild-score/report.
//
// The report is generated in four passes (sequence, then three sections in parallel)
// rather than one call. Each pass keeps its schema small enough that the model stays
// specific to the reader's answers instead of thinning out near the end of a long
// structured response. The section schemas below are the unit of generation; the
// full report is their union.
//
// generateObject rule: every property required, use .nullable() not .optional().
// Array bounds are also restated in prose in report-prompt.ts, because OpenAI's
// strict structured-output mode does not honour minItems/maxItems.

export const REBUILD_DOMAINS = [
  'Routines',
  'Health',
  'Money',
  'Career',
  'Relationships',
  'Identity',
] as const;

export type RebuildDomainName = (typeof REBUILD_DOMAINS)[number];

// The report is a 90-day document, so the schedule has to close inside 90 days rather
// than sprawling past it. Positions 1 to 3 are the ones that fit: 2 weeks for the
// phase-one anchor plus 11 weeks across the next two domains. Positions 4 to 6 are
// mapped honestly as what comes after, with no invented dates.
export const PHASE_ONE_WEEKS = 2;
export const NINETY_DAY_WEEKS = 13;
export const DOMAINS_WITHIN_NINETY_DAYS = 3;

/* ── Pass 1: the sequence ───────────────────────────────────────────────────
   Decided first and alone, because every later pass needs to know which domain
   is position 1 (it gets the 14-day plan) and which are the later phases. */

export const RebuildSequenceSchema = z.object({
  rebuildSequence: z
    .array(
      z.object({
        order: z.number().int().min(1).max(6).describe('Position in the sequence, 1 through 6.'),
        domain: z
          .string()
          .describe('One of: Routines, Health, Money, Career, Relationships, Identity'),
        rationale: z
          .string()
          .describe('2-3 sentences on why this domain sits at this position for this reader.'),
        durationWeeks: z
          .number()
          .int()
          .min(1)
          .max(12)
          .describe('How many weeks to hold this domain before starting the next.'),
        entryCriteria: z
          .string()
          .describe('What must already be true before starting this domain. One sentence.'),
        exitCriteria: z
          .string()
          .describe('The observable signal that this domain is holding and it is safe to move on.'),
      })
    )
    .length(6)
    .describe('All six domains ordered for this reader, position 1 first.'),
});

export type RebuildSequence = z.infer<typeof RebuildSequenceSchema>;

export interface ScheduledRebuildPhase {
  order: number;
  domain: string;
  rationale: string;
  durationWeeks: number;
  entryCriteria: string;
  exitCriteria: string;
  startDay: number;
  endDay: number;
  withinNinetyDays: boolean;
}

/**
 * Turns the model's per-phase durations into actual day ranges. Dates are computed
 * here rather than generated, because arithmetic across six phases is exactly the
 * kind of thing a language model gets quietly wrong, and a report that contradicts
 * its own day-90 markers is worse than one with no dates at all.
 */
export function scheduleRebuildSequence(sequence: RebuildSequence): ScheduledRebuildPhase[] {
  let cursor = 1;
  return [...sequence.rebuildSequence]
    .sort((a, b) => a.order - b.order)
    .map((phase) => {
      const startDay = cursor;
      const endDay = startDay + phase.durationWeeks * 7 - 1;
      cursor = endDay + 1;
      return { ...phase, startDay, endDay, withinNinetyDays: startDay <= 90 };
    });
}

/* ── Pass 2: diagnosis ─────────────────────────────────────────────────────── */

export const ReportDiagnosisSchema = z.object({
  title: z
    .string()
    .describe(
      'Report title naming the archetype, e.g. "Your Life Rebuild Report: The Routine Collapser".'
    ),
  archetype: z.string().describe('The archetype name, exactly as in the free analysis category.'),
  openingLetter: z
    .array(z.string())
    .min(3)
    .max(4)
    .describe(
      '3-4 paragraphs reflecting their answers back and framing the rebuild as a sequence problem.'
    ),
  theStack: z.object({
    summary: z
      .string()
      .describe('2-3 sentences naming how their domains are currently leaning on each other.'),
    chain: z
      .array(
        z.object({
          from: z.string().describe('The domain doing the pushing.'),
          to: z.string().describe('The domain absorbing it.'),
          effect: z.string().describe('One sentence on how the pressure travels between them.'),
        })
      )
      .min(3)
      .max(5)
      .describe('The causal chain between their domains, strongest link first.'),
    loadBearing: z
      .string()
      .describe(
        'The one domain holding the most weight right now, and what happens if it stays unaddressed.'
      ),
  }),
  domainDeepDives: z
    .array(
      z.object({
        domain: z
          .string()
          .describe('One of: Routines, Health, Money, Career, Relationships, Identity'),
        currentState: z
          .string()
          .describe('2-3 sentences on where this domain is, from their answer.'),
        evidence: z
          .string()
          .describe('One sentence naming the specific answer this read is drawn from.'),
        whatItIsCosting: z
          .string()
          .describe('1-2 sentences on the concrete cost of leaving this as it is.'),
        whyNow: z.string().describe('1-2 sentences tied to their decade and mode.'),
        whatGoodLooksLike: z
          .string()
          .describe(
            'A described end state specific enough that they could tell whether they had reached it.'
          ),
        firstMove: z.string().describe('One concrete action they could finish inside a week.'),
      })
    )
    .length(6)
    .describe(
      'One deep dive per domain, in the canonical order: Routines, Health, Money, Career, Relationships, Identity.'
    ),
});

export type ReportDiagnosis = z.infer<typeof ReportDiagnosisSchema>;

/* ── Pass 3: phase one (the domain at position 1) ──────────────────────────── */

export const ReportPhaseOneSchema = z.object({
  phaseOne: z.object({
    domain: z.string().describe('The position-1 domain this phase rebuilds.'),
    intent: z.string().describe('2-3 sentences on what these fourteen days are for.'),
    anchor: z
      .string()
      .describe(
        'The single anchor being defended for the whole fourteen days, stated in one sentence.'
      ),
    days: z
      .array(
        z.object({
          day: z.number().int().min(1).max(14),
          focus: z.string().describe('Short label for the day, at most six words.'),
          action: z
            .string()
            .describe(
              'One specific action, described concretely enough to start without deciding anything else.'
            ),
          minutes: z.number().int().min(5).max(45).describe('Realistic minutes for the action.'),
          note: z
            .string()
            .nullable()
            .describe('A caution or substitution when the day needs one, otherwise null.'),
        })
      )
      .length(14)
      .describe('Fourteen consecutive days, all serving the same anchor.'),
  }),
  weeklyRhythm: z.object({
    summary: z
      .string()
      .describe('1-2 sentences on the repeating weekly shape once phase one is running.'),
    beats: z
      .array(
        z.object({
          name: z.string().describe('Short name for this recurring beat, e.g. "Sunday reset".'),
          when: z.string().describe('When in the week it happens.'),
          what: z.string().describe('What happens in it, in one or two sentences.'),
        })
      )
      .min(3)
      .max(4)
      .describe('The 3-4 recurring beats that hold the week together.'),
  }),
  badWeekProtocol: z.object({
    trigger: z.string().describe('How they will recognise a week that is going sideways.'),
    minimumViable: z
      .string()
      .describe('The smallest version of the anchor that still counts, for a bad week.'),
    recovery: z
      .string()
      .describe('How to resume without treating the gap as a failure. 2-3 sentences.'),
  }),
});

export type ReportPhaseOne = z.infer<typeof ReportPhaseOneSchema>;

/* ── Pass 4: weeks 3 to 13 ─────────────────────────────────────────────────
   The stretch between the end of the 14-day plan and day 90. Without this the
   report hands over daily detail for two weeks and then goes quiet for eleven. */

// Each week gets its own page in the report, so a week has to carry the reasoning
// and the recovery, not just a task list. Thin weeks were the complaint.
export const ReportWeekSchema = z.object({
  week: z.number().int().min(3).max(13).describe('Week number, 3 through 13.'),
  domain: z.string().describe('The sequence domain this week belongs to.'),
  focus: z.string().describe('What this week is for, at most eight words.'),
  whyThisWeek: z
    .string()
    .describe(
      '2-3 sentences on why this week comes here: what it builds on from last week, and what it sets up for next. This is the reasoning, not a restatement of the actions.'
    ),
  insight: z
    .string()
    .describe(
      'One paragraph of coaching specific to this reader and this week. The thing a good coach would say before they start. Not a platitude.'
    ),
  actions: z
    .array(
      z.object({
        title: z.string().describe('Short imperative label, at most eight words.'),
        detail: z
          .string()
          .describe(
            '2-3 sentences on how to actually do it, concrete enough to start without deciding anything else.'
          ),
        minutes: z.number().int().min(5).max(90).describe('Realistic minutes for this action.'),
      })
    )
    .min(3)
    .max(4)
    .describe('3-4 actions for the week.'),
  watchFor: z
    .string()
    .describe(
      'The specific friction most likely to show up in this particular week, and what it looks like.'
    ),
  ifYouSlip: z
    .string()
    .describe(
      'The recovery for this week specifically. What still counts if the week only half happens.'
    ),
  checkpoint: z
    .string()
    .describe('The observable thing that should be true by the end of this week. No feelings.'),
});

export type ReportWeek = z.infer<typeof ReportWeekSchema>;

// Weeks are generated in two chunks so neither structured response gets long enough
// for the later weeks to thin out into slogans.
export const ReportWeeksChunkSchema = z.object({
  weeks: z.array(ReportWeekSchema).min(5).max(6),
});

export const ReportWeekByWeekSchema = z.object({
  weekByWeek: z
    .array(ReportWeekSchema)
    .length(11)
    .describe('Weeks 3 through 13 in order, covering days 15 to 91.'),
});

export type ReportWeekByWeek = z.infer<typeof ReportWeekByWeekSchema>;

export const WEEK_CHUNKS: ReadonlyArray<{ from: number; to: number }> = [
  { from: 3, to: 8 },
  { from: 9, to: 13 },
];

/* ── Pass 5: beyond 90 days, markers, and close ───────────────────────────── */

export const ReportLaterPhasesSchema = z.object({
  beyondNinetyDays: z
    .array(
      z.object({
        order: z.number().int().min(4).max(6).describe('Position in the sequence, 4 through 6.'),
        domain: z.string(),
        whenYouGetHere: z
          .string()
          .describe(
            '1-2 sentences on what should already be true when this phase starts. No invented dates.'
          ),
        firstThreeMoves: z
          .array(z.string())
          .length(3)
          .describe('The first three concrete moves for this domain, in order.'),
        whatGoodLooksLike: z.string().describe('The observable end state for this phase.'),
        commonFailure: z
          .string()
          .describe('The most likely way this specific phase goes wrong, and the correction.'),
      })
    )
    .length(3)
    .describe('Phases 4 through 6, the ones that start after the first 90 days.'),
  milestones: z
    .array(
      z.object({
        day: z.number().int().describe('30, 60, or 90'),
        marker: z.string().describe('Something observable that should be true by this day.'),
        howToMeasure: z
          .string()
          .describe('How they will actually check this, in one sentence. No vague feelings.'),
      })
    )
    .length(3)
    .describe('Day 30, 60, and 90, in that order.'),
  watchOuts: z
    .array(
      z.object({
        pattern: z.string().describe('The derailment, named in a few words.'),
        whyItHappens: z.string().describe('Why this archetype in particular falls into it.'),
        whatToDoInstead: z.string().describe('The specific correction.'),
      })
    )
    .min(3)
    .max(5)
    .describe('The 3-5 ways this reader is most likely to derail.'),
  reflectionPrompts: z
    .array(z.string())
    .length(5)
    .describe(
      'Five reusable check-in questions written for this reader, not generic journalling prompts.'
    ),
  closingNote: z.string().describe('One short paragraph ending on one of the canonical lines.'),
});

export type ReportLaterPhases = z.infer<typeof ReportLaterPhasesSchema>;

/* ── The assembled report ──────────────────────────────────────────────────── */

export const LifeRebuildReportSchema = z.object({
  ...ReportDiagnosisSchema.shape,
  ...RebuildSequenceSchema.shape,
  ...ReportPhaseOneSchema.shape,
  ...ReportWeekByWeekSchema.shape,
  ...ReportLaterPhasesSchema.shape,
});

export type LifeRebuildReport = z.infer<typeof LifeRebuildReportSchema>;

export type { VisitorReportStatus } from '../../paid-reports';

// The shared `visitor_reports/{runId}` shapes, narrowed to this report.
export type VisitorReportDoc = PaidVisitorReportDoc<LifeRebuildReport>;
export type VisitorReportResponse = PaidVisitorReportResponse<LifeRebuildReport>;

export const LIFE_REBUILD_REPORT_PRICE_USD = 19;
export const LIFE_REBUILD_REPORT_TASK_ID = 'life-rebuild-report-generator';
